/**
 * Đọc dữ liệu POS gộp / ưu tiên CUKCUK sau khi tách collection.
 * - Hóa đơn: gộp iPOS + CUKCUK; dòng bán trùng chéo nguồn ưu tiên CUKCUK
 * - Trong một nguồn: dedupe bằng lineKey, sau đó id; thiếu cả hai thì giữ nguyên số dòng
 * - Menu / customization cho công thức & xuất NL: ưu tiên CUKCUK
 */
import type { Customization } from '../types/customization';
import type { Dish } from '../types/dish';
import type { InvoiceNotification } from '../types/invoiceNotification';
import {
  createCukcukCustomizationService,
  createCukcukDishService,
  createCukcukInvoiceNotificationService,
} from './cukcukDataServices';
import { CustomizationService } from './CustomizationService';
import { DishService } from './dishService';
import { InvoiceNotificationService } from './invoiceNotificationService';

const tagSource = (
  rows: InvoiceNotification[],
  source: 'ipos' | 'cukcuk',
): InvoiceNotification[] =>
  rows.map((row) => ({
    ...row,
    rawData: {
      ...(row.rawData || {}),
      source,
    },
  }));

export type PosInvoiceSource = 'ipos' | 'cukcuk';
export type PosInvoiceSourceLoad = { status: 'loaded' | 'failed'; rowCount: number };
export interface MergedInvoiceNotificationsResult {
  rows: InvoiceNotification[];
  sources: Record<PosInvoiceSource, PosInvoiceSourceLoad>;
  warnings: string[];
}

const sourceWarning: Record<PosInvoiceSource, string> = {
  ipos: 'Không thể tải hóa đơn iPOS. Dữ liệu đang hiển thị có thể chưa đầy đủ.',
  cukcuk: 'Không thể tải hóa đơn CUKCUK. Dữ liệu đang hiển thị có thể chưa đầy đủ.',
};

const normalizeFingerprintPart = (value: unknown): string =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/** Chỉ tạo khóa khi có đủ mã giao dịch, ngày và món để tránh gộp nhầm các dòng yếu. */
const getCrossSourceLineFingerprint = (row: InvoiceNotification): string | null => {
  const transactionCode = normalizeFingerprintPart(row.invoiceCode || row.orderCode);
  const saleDate = normalizeFingerprintPart(row.checkInDate || row.saleDate);
  const itemIdentity = normalizeFingerprintPart(row.itemId || row.itemName);
  if (!transactionCode || !saleDate || !itemIdentity) return null;

  const options = [...(row.selectedOptions || [])]
    .map((option) => [
      normalizeFingerprintPart(option.optionId || option.optionName),
      Number(option.quantity || 0),
      Number(option.amount || option.price || 0),
    ].join(':'))
    .sort()
    .join(',');

  return [
    transactionCode,
    saleDate,
    normalizeFingerprintPart(row.checkInTime),
    itemIdentity,
    Number(row.quantity || 0),
    Number(row.unitPrice || 0),
    Number(row.lineTotal || 0),
    options,
  ].join('|');
};

const loadInvoiceSource = async (
  source: PosInvoiceSource,
  load: () => Promise<InvoiceNotification[]>,
): Promise<{ rows: InvoiceNotification[]; load: PosInvoiceSourceLoad; warning?: string }> => {
  try {
    const rows = await load();
    return { rows, load: { status: 'loaded', rowCount: rows.length } };
  } catch {
    return { rows: [], load: { status: 'failed', rowCount: 0 }, warning: sourceWarning[source] };
  }
};

/** Hóa đơn iPOS + CUKCUK, kèm trạng thái từng nguồn để nhận biết dữ liệu thiếu. */
export const getMergedInvoiceNotificationsWithStatus = async (): Promise<MergedInvoiceNotificationsResult> => {
  const iposService = new InvoiceNotificationService();
  const cukcukService = createCukcukInvoiceNotificationService();
  const [ipos, cukcuk] = await Promise.all([
    loadInvoiceSource('ipos', () => iposService.getAllNotifications()),
    loadInvoiceSource('cukcuk', () => cukcukService.getAllNotifications()),
  ]);
  const merged = [...tagSource(ipos.rows, 'ipos'), ...tagSource(cukcuk.rows, 'cukcuk')];
  const byKey = new Map<string, InvoiceNotification>();

  merged.forEach((row, index) => {
    const source = row.rawData?.source as PosInvoiceSource;
    const identity = row.lineKey
      ? `lineKey:${row.lineKey}`
      : row.id
        ? `id:${row.id}`
        : `unidentified:${index}`;
    const key = `${source}:${identity}`;
    if (!byKey.has(key)) byKey.set(key, row);
  });

  const sourceDedupedRows = Array.from(byKey.values());
  const availableCukcukFingerprints = new Map<string, number>();
  sourceDedupedRows.forEach((row) => {
    if (row.rawData?.source !== 'cukcuk') return;
    const fingerprint = getCrossSourceLineFingerprint(row);
    if (!fingerprint) return;
    availableCukcukFingerprints.set(fingerprint, (availableCukcukFingerprints.get(fingerprint) || 0) + 1);
  });

  const duplicateIposIndexes = new Set<number>();
  sourceDedupedRows.forEach((row, index) => {
    if (row.rawData?.source !== 'ipos') return;
    const fingerprint = getCrossSourceLineFingerprint(row);
    if (!fingerprint) return;
    const counterpartCount = availableCukcukFingerprints.get(fingerprint) || 0;
    if (counterpartCount <= 0) return;
    duplicateIposIndexes.add(index);
    availableCukcukFingerprints.set(fingerprint, counterpartCount - 1);
  });

  return {
    rows: sourceDedupedRows.filter((_, index) => !duplicateIposIndexes.has(index)),
    sources: { ipos: ipos.load, cukcuk: cukcuk.load },
    warnings: [ipos.warning, cukcuk.warning].filter((warning): warning is string => !!warning),
  };
};

/** API cũ: chỉ trả danh sách hóa đơn. */
export const getMergedInvoiceNotifications = async (): Promise<InvoiceNotification[]> =>
  (await getMergedInvoiceNotificationsWithStatus()).rows;

/** Menu chính sau chuyển CUKCUK. Fallback iPOS nếu bảng CUKCUK còn trống. */
export const getPreferredMenuDishes = async (): Promise<{
  dishes: Dish[];
  source: 'cukcuk' | 'ipos';
}> => {
  const cukcukDishes = await createCukcukDishService()
    .getAllDishes()
    .catch(() => [] as Dish[]);

  const mainDishes = cukcukDishes.filter((dish) => !dish.isVirtualDish);
  if (mainDishes.length > 0 || cukcukDishes.length > 0) {
    return { dishes: cukcukDishes, source: 'cukcuk' };
  }

  const iposDishes = await new DishService().getAllDishes().catch(() => [] as Dish[]);
  return { dishes: iposDishes, source: 'ipos' };
};

/** Customization option theo nguồn menu (CUKCUK ưu tiên). */
export const getPreferredCustomizations = async (
  menuSource: 'cukcuk' | 'ipos' = 'cukcuk',
): Promise<Customization[]> => {
  if (menuSource === 'cukcuk') {
    const rows = await createCukcukCustomizationService()
      .getAllCustomizations()
      .catch(() => [] as Customization[]);
    if (rows.length > 0) return rows;
  }

  return new CustomizationService().getAllCustomizations().catch(() => [] as Customization[]);
};
